import React from 'react';

import { useClassnames } from '@lumapps/classnames';
import { useDataAttributes } from '@lumapps/data-attributes';
import { mdiClose } from '@lumapps/lumx/icons';
import { Placement, Popover } from '@lumapps/lumx/react';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { ElementToolbar } from '@lumapps/wrex/components/ElementToolbar';
import { TOOLBAR_ITEM_TYPE, type ToolbarItem as ToolbarItemType } from '@lumapps/wrex/types';

import { SCOPE } from '../../../constants';
import { WREX_COLUMNS } from '../../../keys';

const CLASSNAME = 'wrex-content-columns-toolbar';

interface ColumnsToolbarProps {
    /** anchor ref for the toolbar */
    anchorRef: React.RefObject<HTMLElement>;
    /** whether the toolbar is opened */
    isOpen: boolean;
    /** callback when clicking on delete action button */
    onDelete: () => void;
}

/**
 * Toolbar with actions on columns block
 */
export const ColumnsToolbar: React.FC<ColumnsToolbarProps> = ({ anchorRef, isOpen, onDelete }) => {
    const { block } = useClassnames(CLASSNAME);
    const { translateKey } = useTranslate();
    const { get } = useDataAttributes(SCOPE);
    const deleteButtonRef = React.useRef<HTMLDivElement>(null);

    const staticOptions: ToolbarItemType[] = React.useMemo(
        () => [
            {
                type: TOOLBAR_ITEM_TYPE.OPTION,
                icon: mdiClose,
                itemKey: 'delete',
                variant: 'icon',
                label: translateKey(GLOBAL.DELETE),
                onClick: onDelete,
                ref: deleteButtonRef,
                otherProps: get({ element: 'button', action: 'delete' }),
            },
        ],
        [get, onDelete, translateKey],
    );

    return (
        <Popover
            contentEditable={false}
            className={block()}
            anchorRef={anchorRef}
            placement={Placement.TOP_END}
            isOpen={isOpen}
            offset={{ away: 8 }}
            zIndex={10}
            usePortal={false}
            closeOnClickAway
        >
            <ElementToolbar
                toolbarOptions={[]}
                staticOptions={staticOptions}
                toolbarAriaLabel={WREX_COLUMNS.TOOLBAR}
                dataScope={SCOPE}
                currentBreakpoint="full"
                menuBarRef={deleteButtonRef}
                shouldBeDisplayedOutsideElement
            />
        </Popover>
    );
};
